import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Trash2, Mail } from "lucide-react";

const TABS = [
    { key: "contact", label: "Contact" },
    { key: "volunteer", label: "Volunteer" },
    { key: "csr", label: "CSR" },
    { key: "donation", label: "Donation" },
];

const HIDE = ["id", "created_at", "reviewed_at", "reviewed"];

export default function AdminEnquiries() {
    const [tab, setTab] = useState("contact");
    const [items, setItems] = useState([]);

    const load = useCallback(
        () => api.get(`/admin/enquiries/${tab}`).then((r) => setItems(r.data || [])),
        [tab]
    );
    useEffect(() => { load(); }, [load]);

    const rows = useMemo(
        () =>
            items.map((e) => ({
                ...e,
                _entries: Object.entries(e).filter(
                    ([k, v]) => !HIDE.includes(k) && v !== null && v !== undefined && v !== ""
                ),
            })),
        [items]
    );

    const review = async (id) => {
        await api.post(`/admin/enquiries/${tab}/${id}/review`);
        toast.success("Marked reviewed");
        load();
    };
    const remove = async (id) => {
        if (!window.confirm("Delete this enquiry?")) return;
        await api.delete(`/admin/enquiries/${tab}/${id}`);
        load();
    };

    return (
        <div data-testid="admin-enquiries">
            <h1 className="text-2xl font-semibold mb-1">Enquiries</h1>
            <p className="text-sm text-slate-500 mb-6">Submissions from every form on the site.</p>
            <div className="flex gap-2 mb-6 border-b border-slate-200">
                {TABS.map((t) => (
                    <button
                        key={t.key}
                        onClick={() => setTab(t.key)}
                        data-testid={`enquiry-tab-${t.key}`}
                        className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px ${
                            tab === t.key ? "text-brand-forest border-brand-forest" : "text-slate-500 border-transparent hover:text-slate-700"
                        }`}
                    >{t.label}</button>
                ))}
            </div>

            <div className="space-y-3">
                {rows.length === 0 && <div className="text-slate-500 text-sm py-12 text-center">No enquiries yet.</div>}
                {rows.map((e) => (
                    <div key={e.id} className={`bg-white border rounded-lg p-5 ${e.reviewed ? "border-slate-200" : "border-brand-forest/40"}`}>
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3 mb-2">
                                    <div className="font-semibold">{e.name || e.contact_name || e.organization}</div>
                                    {!e.reviewed && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded">New</span>}
                                    <div className="text-xs text-slate-500">{new Date(e.created_at).toLocaleString()}</div>
                                </div>
                                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                                    {e._entries.map(([k, v]) => (
                                        <div key={k} className="flex gap-2">
                                            <span className="text-slate-500 min-w-[110px] capitalize">{k.replace(/_/g, " ")}:</span>
                                            <span className="text-slate-800 break-words">{String(v)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="flex flex-col gap-1 flex-shrink-0">
                                {e.email && <a href={`mailto:${e.email}`} className="p-2 hover:bg-slate-100 rounded" title="Reply"><Mail size={14} /></a>}
                                {!e.reviewed && <button onClick={() => review(e.id)} className="p-2 hover:bg-emerald-50 text-emerald-700 rounded" title="Mark reviewed"><CheckCircle2 size={14} /></button>}
                                <button onClick={() => remove(e.id)} className="p-2 hover:bg-red-50 text-red-600 rounded" title="Delete"><Trash2 size={14} /></button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
